import { AccommodationsSearchRequestDto } from '@/features/AccommodationList/api/requestTypes';
import {
  AccommodationSearchItemDto,
  OfferDto
} from '@/features/AccommodationList/api/responseTypes';
import { Location } from '@/features/AccommodationList/components/AccommodationMap';
import { FlightsRequest } from '@/features/TransportList/api/requestTypes';
import { FlightsResponse } from '@/features/TransportList/api/responseTypes';
import { act, useMemo } from 'react';
import ReactGA from 'react-ga4';

import { getStoredConsent } from '@/infrastructure/consent/consentManager';

type Event = {
  category: string;
  action: string;
  label?: string; // optional
  value?: number; // optional, must be a number
  nonInteraction?: boolean; // optional, true/false
  transport?: 'beacon' | 'xhr' | 'image'; // optional, beacon/xhr/image
};

const useTrackEvent = () => {
  const referrerInfo = useMemo(() => {
    try {
      if (!document.referrer) return { domain: 'direct', fullPath: 'direct' };

      const referrerUrl = new URL(document.referrer);
      return {
        domain: referrerUrl.hostname,
        fullPath: referrerUrl.hostname + referrerUrl.pathname
      };
    } catch (e) {
      return { domain: 'invalid_referrer', fullPath: 'invalid_referrer' };
    }
  }, []);

  const trackEvent = (action: string, event: any) => {
    const consent = getStoredConsent();
    if (!consent?.categories?.analytics) {
      if (import.meta.env.DEV) {
        console.debug(
          `[GA4 Gated] Suppressed event '${action}' because Analytics consent is not granted.`
        );
      }
      return;
    }
    if (import.meta.env.DEV) {
      console.debug({
        action,
        message: {
          ...event,
          action,
          category: 'Widget',
          referrer_domain: referrerInfo.domain,
          referrer_full_path: referrerInfo.fullPath
        }
      });
    }
    ReactGA.event({
      ...event,
      action,
      category: 'Widget',
      referrer_domain: referrerInfo.domain,
      referrer_full_path: referrerInfo.fullPath
    });
  };

  const trackWidgetInitEvent = () => {
    trackEvent('widget_loaded', {
      category: 'Widget',
      label: referrerInfo.domain
    });
  };

  const trackAccommodationSearch = (
    searchDetails: AccommodationsSearchRequestDto
  ) => {
    trackEvent('accommodation_search', {
      label:
        searchDetails.location?.city +
        ' ' +
        searchDetails.location?.country +
        ' ' +
        searchDetails.location?.place,
      search_term: searchDetails.location,
      check_in: searchDetails.checkIn,
      check_out: searchDetails.checkOut,
      guests: searchDetails.travelers,
      location_search_term: searchDetails.locationSearchTerm,
      center_point: searchDetails.centerPoint,
      radius_in_meters: searchDetails.radiusInMeters
    });
  };

  const trackAccommodationMapSearch = (
    searchDetails: AccommodationsSearchRequestDto
  ) => {
    trackEvent('accommodation_search', {
      map_search: true,
      label: searchDetails.location,
      search_term: searchDetails.location,
      check_in: searchDetails.checkIn,
      check_out: searchDetails.checkOut,
      guests: searchDetails.travelers,
      location_search_term: searchDetails.locationSearchTerm,
      center_point: searchDetails.centerPoint,
      radius_in_meters: searchDetails.radiusInMeters
    });
  };

  const trackAccommodationBooking = accommodationBookingEventDetails => {
    trackEvent('accommodation_booking', {
      label: accommodationBookingEventDetails.hotelName,
      hotel_name: accommodationBookingEventDetails.hotelName,
      room_types: accommodationBookingEventDetails.roomTypes,
      price: accommodationBookingEventDetails.price,
      currency: accommodationBookingEventDetails.currency
    });
  };

  const trackFlightBooking = (
    flightBookingEventDetails: FlightsRequest,
    flightItem: FlightsResponse
  ) => {
    trackEvent('flight_booking', {
      label:
        flightBookingEventDetails.origin +
        '-' +
        flightBookingEventDetails.destination,
      price: flightItem.amount,
      currency: flightItem.currency
    });
  };

  const trackSelectAccommodation = (hotel: AccommodationSearchItemDto) => {
    trackEvent('select_accommodation', {
      label: hotel.hotelName,
      hotel_name: hotel.hotelName,
      id: hotel.id,
      price: hotel.price,
      currency: hotel.currency
    });
  };

  const trackAccommodationListItemClick = (
    hotel: AccommodationSearchItemDto,
    index: number
  ) => {
    trackEvent('accommodation_list_item_click', {
      label: hotel.hotelName,
      hotel_name: hotel.hotelName,
      id: hotel.id,
      position_in_list: index,
      price: hotel.price,
      currency: hotel.currency
    });
  };

  const trackAccommodationMapItemClick = (
    hotel: AccommodationSearchItemDto,
    location: Location
  ) => {
    trackEvent('accommodation_map_item_click', {
      label: hotel.hotelName,
      hotel_name: hotel.hotelName,
      id: hotel.id,
      latitude: location.lat,
      longitude: location.lng,
      price: location.price,
      currency: location.currency
    });
  };

  const trackAccommodationOfferSelected = (
    offer: OfferDto,
    hotel: AccommodationSearchItemDto
  ) => {
    trackEvent('accommodation_offer_selected', {
      label: hotel.hotelName,
      room_types: offer.rooms.map(r => r.roomCode).join('|'),
      room_names: offer.rooms.map(r => r.roomName).join('|'),
      price: offer.price,
      currency: offer.currency,
      hotel_id: hotel.id,
      hotel_name: hotel.hotelName,
      value: offer.price
    });
  };

  return {
    trackEvent,
    trackWidgetInitEvent,
    trackAccommodationSearch,
    trackAccommodationMapSearch,
    trackAccommodationBooking,
    trackFlightBooking,
    trackSelectAccommodation,
    trackAccommodationListItemClick,
    trackAccommodationMapItemClick,
    trackAccommodationOfferSelected
  };
};

export default useTrackEvent;
